<template>
	<div class="text-center">
		<v-container>
			<div v-if="inEditMode && !newInspection" :class="['text-h5', 'pa-4']">
				{{ $t('titles.editingInspection') }}
			</div>
			<div v-if="closingInspection" :class="['text-h5', 'pa-4']">{{ $t('titles.closingInspection') }}</div>
			<v-row>
				<v-col cols="6">
					<InputField
						:readonly="!inEditMode"
						v-model="inspection.name"
						:errorBorder="!inspectionValidation.name"
						:label="'* ' + $t('fields.name')"
					></InputField>
				</v-col>
				<v-col cols="6">
					<DatePicker
						:readonly="!inEditMode"
						v-model="inspection.due_date"
						:errorBorder="!inspectionValidation.due_date"
						:date-label="'* ' + $t('fields.date')"
					></DatePicker>
				</v-col>
				<v-col cols="12">
					<InputField
						:readonly="!inEditMode"
						v-model="inspection.activity_description"
						:errorBorder="!inspectionValidation.activity_description"
						:label="$t('fields.activityDescription')"
					></InputField>
				</v-col>
				<v-col cols="6">
					<Dropdown3
						:readonly="!inEditMode"
						v-model="inspection.inspection_frequency_id"
						:ajax="true"
						:data="'inspection-frequencies'"
						:errorBorder="!inspectionValidation.inspection_frequency_id"
						:label="'* ' + $t('fields.inspectionFrequency')"
					></Dropdown3>
				</v-col>
				<v-col cols="5" v-if="closingInspection && inspection.inspection_frequency_id > 1">
					<DatePicker v-model="newDueDate" :date-label="'* ' + $t('fields.newInspectionDate')"></DatePicker>
				</v-col>
				<v-col cols="1" class="mt-4" v-if="closingInspection && inspection.inspection_frequency_id > 1">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn icon v-bind="attrs" @click="clearNewDueDate()" v-on="on">
								<v-icon color="red" small>fas fa-trash</v-icon>
							</v-btn>
						</template>
						<span>{{ $t('tooltips.deleteNextInspectionDate') }}</span>
					</v-tooltip>
				</v-col>
			</v-row>

			<v-row v-if="inEditMode">
				<v-col cols="12">
					<v-row>
						<v-col cols="10" v-for="(field, index) in inspection.uploaded_files" :key="index">
							<Upload
								:readonly="false"
								v-model="inspection.uploaded_files[index]"
								:label="$t('fields.file')"
							></Upload>
						</v-col>
						<v-col cols="2">
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<v-btn v-bind="attrs" color="basicButton" @click="newUploadField()" v-on="on">
										<v-icon small>fa-plus</v-icon>
									</v-btn>
								</template>
								<span>{{ $t('tooltips.addNewDocument') }}</span>
							</v-tooltip>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
			<v-row v-if="!inEditMode">
				<v-col cols="3" v-for="(field, index) in inspection.uploaded_files" :key="index">
					<Upload :readonly="true" v-model="inspection.uploaded_files[index]"></Upload>
				</v-col>
			</v-row>
		</v-container>
		<v-spacer></v-spacer>
		<v-row>
			<v-col cols="12" v-if="inEditMode || closingInspection">
				<v-btn class="ma-2" color="primary" variant="text" @click="saveClick"> {{ $t('buttons.save') }} </v-btn>
				<v-btn class="ma-2" color="basicButton" variant="text" @click="cancelClick">
					{{ $t('buttons.cancel') }}
				</v-btn>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import Inspection from '../../../models/Inspection';
import Dropdown3 from '../../trinkets/Dropdown3';
import DatePicker from '../../trinkets/DatePicker';
import InputField from '../../trinkets/InputField';
import Upload from '../../trinkets/Upload';

export default {
	name: 'Inspection',
	components: { InputField, Dropdown3, DatePicker, Upload },
	props: {
		inspection: {
			type: Object,
			default: function () {
				return new Inspection({ equipment_id: this.$route.params.id });
			},
		},
		closingInspection: Boolean,
		inEditMode: Boolean,
		newInspection: Boolean,
	},
	emits: ['close', 'reloadActivities', 'reloadInspections'],
	data() {
		return {
			inspectionValidation: new Inspection({}, true),
			newDueDate: null,
		};
	},
	watch: {
		closingInspection(newValue) {
			if (newValue == true) {
				this.newDueDate = this.createNewDueDate(
					this.inspection.inspection_frequency_id,
					this.inspection.due_date
				);
			}
		},
	},
	mounted() {
		if (this.closingInspection) {
			this.newDueDate = this.createNewDueDate(this.inspection.inspection_frequency_id, this.inspection.due_date);
		}
	},
	methods: {
		cancelClick() {
			this.newDueDate = null;
			this.$emit('close');
		},
		newUploadField() {
			this.inspection.uploaded_files.push({
				name: '',
				link: '',
				original_name: '',
				upload_id: null,
			});
		},

		removeEmptyObject(arr) {
			return arr.filter((obj) => {
				for (let key in obj) {
					if (obj.hasOwnProperty(key)) {
						if (obj[key] === '') {
							return false;
						}
					}
				}
				return true;
			});
		},
		saveClick() {
			let success = true;
			let errorMessages = '';
			if (this.closingInspection) {
				this.inspection.closed = true;
				this.inspection.user_id = this.$myStore.state.user.id;
				if (this.newDueDate) {
					this.inspection.create_new_inspection = true;
					this.inspection.new_due_date = this.newDueDate;
				}
			} else {
				if (this.inspection.name == null || this.inspection.name == '') {
					success = false;
					this.inspectionValidation.name = false;
				} else {
					if (this.inspection.name.length > 50) {
						this.inspectionValidation.name = false;
						errorMessages +=
							this.$t('alerts.lengthError')
								.replace('%field%', this.$t('fields.name'))
								.replace('%length%', '50') + '<br />';
					} else {
						this.inspectionValidation.name = true;
					}
				}

				if (this.inspection.activity_description != null && this.inspection.activity_description.length > 200) {
					this.inspectionValidation.activity_description = false;
					errorMessages +=
						this.$t('alerts.lengthError')
							.replace('%field%', this.$t('fields.activity_description'))
							.replace('%length%', '200') + '<br />';
				} else {
					this.inspectionValidation.activity_description = true;
				}

				if (!this.inspection.due_date) {
					success = false;
					this.inspectionValidation.due_date = false;
				} else {
					this.inspectionValidation.due_date = true;
				}

				if (!this.inspection.inspection_frequency_id) {
					success = false;
					this.inspectionValidation.inspection_frequency_id = false;
				} else {
					this.inspectionValidation.inspection_frequency_id = true;
				}

				if (errorMessages !== '') {
					this.$alert(errorMessages);
					success = false;
				}
			}

			if (success) {
				let vm = this;
				let action = 'post';
				let addition = '';
				if (vm.inspection.id) {
					action = 'put';
					addition = '/' + vm.inspection.id;
				}
				if (this.inspection.uploaded_files) {
					this.inspection.uploaded_files = this.removeEmptyObject(this.inspection.uploaded_files);
				}
				this.$APICall(action, 'inspections' + addition, this.inspection, (data, success) => {
					if (success) {
						if (vm.inspection.id == 0) {
							vm.$notify(vm.$t('alerts.basicAdd'));
						} else {
							vm.$notify(vm.$t('alerts.basicUpdate'));
						}
						vm.$modalBus.$emit('reloadActivities');
						vm.$modalBus.$emit('reloadInspections');
					} else {
						vm.$alert(vm.$t('alerts.basicError'));
					}
				});
				this.$emit('close');
			}
		},
		clearNewDueDate() {
			this.newDueDate = null;
		},
		createNewDueDate(frequencyType, originalDate) {
			if (frequencyType == 2) {
				return this.increaseByWeek(originalDate);
			} else if (frequencyType == 3) {
				return this.increaseByMonth(originalDate);
			} else if (frequencyType == 4) {
				return this.increaseByHalfYear(originalDate);
			} else if (frequencyType == 5) {
				return this.increaseByYear(originalDate);
			} else {
				return null;
			}
		},
		increaseByWeek(originalDate) {
			const original = new Date(originalDate);
			const increased = new Date(original.getTime() + 7 * 24 * 60 * 60 * 1000);
			return increased.toISOString().split('T')[0];
		},
		increaseByMonth(originalDate) {
			const original = new Date(originalDate);
			original.setMonth(original.getMonth() + 1);
			return original.toISOString().split('T')[0];
		},
		increaseByHalfYear(originalDate) {
			const original = new Date(originalDate);
			original.setMonth(original.getMonth() + 6);
			return original.toISOString().split('T')[0];
		},
		increaseByYear(originalDate) {
			const original = new Date(originalDate);
			original.setFullYear(original.getFullYear() + 1);
			return original.toISOString().split('T')[0];
		},
	},
};
</script>
